import { Module } from "vuex";
import { IRootState } from "@/store";
import { baseApi } from "@/config/http-common";
import { AccountIdentity, accountSessionMatches } from "@/utils/accountSession";

export interface ILoaderState {
  isUserLoggedIn: boolean;
  userIdentity: AuthIdentity | null;
}

export interface AuthIdentity {
  id: string;
  login: string;
  email: string;
  tenant?: AccountIdentity["tenant"];
  token?: string;
  refreshToken?: string;
  username?: string;
  roleIds?: string[];
  roles?: string[];
  permissions?: string[];
  systemConfig?: Record<string, boolean>;
  active?: boolean;
  platformAdmin?: boolean;
}

interface AuthContext {
  tenant?: AccountIdentity["tenant"];
  userId: string;
  login: string;
  email: string;
  active: boolean;
  platformAdmin: boolean;
  roleIds: string[];
  roles: string[];
  permissions: string[];
  systemConfig: Record<string, boolean>;
}

export enum AuthActions {
  SET_IS_LOGGED_IN = "SET_LOGIN",
  LOGIN = "LOGIN",
  LOGOUT = "LOGOUT",
  LOGIN_BY_ECP = "LOGIN_BY_ECP",
  UPDATE_TOKEN = "UPDATE_TOKEN",
  REFRESH_CONTEXT = "REFRESH_AUTH_CONTEXT",
}

export enum AuthMutations {
  CHANGE_IS_LOGGED_IN = "CHANGE_IS_LOGGED_IN",
  SET_USER_IDENTITY = "SET_USER_IDENTITY",
}

export enum AuthGetters {
  GET_IS_LOGGED_IN_IN = "GET_IS_LOGGED_IN_IN",
  GET_REFRESH_TOKEN = "GET_REFRESH_TOKEN",
  GET_USER_IDENTITY = "GET_USER_IDENTITY",
}

function getStoredIdentity(): AuthIdentity | null {
  const storedUser = localStorage.getItem("user");
  if (!storedUser) {
    return null;
  }

  try {
    return JSON.parse(storedUser) as AuthIdentity;
  } catch {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    return null;
  }
}

function setUserLocalStorageData(user: AuthIdentity, token: string) {
  localStorage.setItem("user", JSON.stringify(user));
  localStorage.setItem("token", token);
}

export const auth: Module<ILoaderState, IRootState> = {
  actions: {
    [AuthActions.SET_IS_LOGGED_IN]({ commit }, payload) {
      commit(AuthMutations.CHANGE_IS_LOGGED_IN, payload);
    },
    async [AuthActions.LOGIN]({ commit }, { login, password }) {
      const response = await baseApi.post("common/auth/login", {
        login,
        password,
      });
      const identity = response.data.data as AuthIdentity;
      setUserLocalStorageData(identity, identity.token || "");
      commit(AuthMutations.SET_USER_IDENTITY, identity);
      commit(AuthMutations.CHANGE_IS_LOGGED_IN, true);
      return identity;
    },
    [AuthActions.LOGOUT]({ commit }) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      commit(AuthMutations.SET_USER_IDENTITY, null);
      commit(AuthMutations.CHANGE_IS_LOGGED_IN, false);
    },
    async [AuthActions.LOGIN_BY_ECP]({ commit }, { sign, login }) {
      return new Promise((resolve, reject) => {
        baseApi
          .post("common/auth/login-ecp", {
            sign,
            login,
          })
          .then((response) => {
            setUserLocalStorageData(
              response.data.data,
              response.data.data.token
            );
            commit(AuthMutations.SET_USER_IDENTITY, response.data.data);
            commit(AuthMutations.CHANGE_IS_LOGGED_IN, true);

            resolve(response.data.data as AuthIdentity);
          })
          .catch((error) => {
            console.log("job create error: ", error);
            reject(error.response);
          });
      });
    },
    async [AuthActions.UPDATE_TOKEN]({ commit, getters }, payload) {
      return new Promise((resolve, reject) => {
        baseApi
          .post(`common/auth/refresh-token`, payload)
          .then((result) => {
            const token = result.data.data.token;

            const user = getters[AuthGetters.GET_USER_IDENTITY];
            user.token = token;

            setUserLocalStorageData(user, token);
            commit(AuthMutations.SET_USER_IDENTITY, user);

            resolve(true);
          })
          .catch((error) => {
            reject(error.response);
          });
      });
    },
    async [AuthActions.REFRESH_CONTEXT]({ commit, getters }) {
      const currentIdentity = getters[
        AuthGetters.GET_USER_IDENTITY
      ] as AuthIdentity | null;
      if (!currentIdentity?.token) {
        throw new Error("Authenticated user context is unavailable");
      }

      const response = await baseApi.get<AuthContext>("common/auth/context");
      const context = response.data;
      const refreshedIdentity: AuthIdentity = {
        ...currentIdentity,
        id: context.userId,
        login: context.login,
        username: context.login,
        email: context.email,
        active: context.active,
        platformAdmin: context.platformAdmin,
        tenant: context.tenant ?? null,
        roleIds: context.roleIds || [],
        roles: context.roles || [],
        permissions: context.permissions || [],
        systemConfig: context.systemConfig || {},
      };

      setUserLocalStorageData(refreshedIdentity, currentIdentity.token);
      commit(AuthMutations.SET_USER_IDENTITY, refreshedIdentity);
      return refreshedIdentity;
    },
  },
  mutations: {
    [AuthMutations.CHANGE_IS_LOGGED_IN](state, status) {
      state.isUserLoggedIn = status;
    },
    [AuthMutations.SET_USER_IDENTITY](state, identity: AuthIdentity | null) {
      state.userIdentity = identity;
    },
  },
  getters: {
    [AuthGetters.GET_IS_LOGGED_IN_IN](state): boolean {
      return state.isUserLoggedIn;
    },
    [AuthGetters.GET_REFRESH_TOKEN](state): string | null {
      return state.userIdentity?.refreshToken || null;
    },
    [AuthGetters.GET_USER_IDENTITY](state): AuthIdentity | null {
      return state.userIdentity;
    },
  },
  state: {
    isUserLoggedIn: false,
    userIdentity: getStoredIdentity(),
  },
};

/** Checked by every HTTP client, including file and notification requests. */
export function getAccountSessionToken(
  identity: AuthIdentity | null
): string | undefined {
  if (!accountSessionMatches(identity)) {
    window.location.replace("/login");
    throw new Error(
      "The account changed in another tab; reload before continuing"
    );
  }
  return identity?.token;
}
